import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { intlLocaleFor } from "@/lib/appLocale";
import { resolveCleaningReleasedPropertyIssue } from "@/lib/cleaningIssueApi";
import {
  canCleaningManagementSelfResolve,
  issueStatusBadgeClass,
  type CleaningIssueHistoryRow,
} from "@/lib/issueStatusLabels";

type CleaningIssueHistoryTableProps = {
  issues: CleaningIssueHistoryRow[];
  canEdit: boolean;
  onResolved: () => void | Promise<void>;
};

export function CleaningIssueHistoryTable({
  issues,
  canEdit,
  onResolved,
}: CleaningIssueHistoryTableProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = intlLocaleFor(i18n.language);
  const [issueToResolve, setIssueToResolve] = useState<CleaningIssueHistoryRow | null>(null);
  const [isResolving, setIsResolving] = useState(false);

  const statusText = (status: string | null) => {
    if (!status) return "—";
    return t(`admin.issuesHistory.statusLabel.${status}`, { defaultValue: status });
  };

  const handleConfirmResolve = async () => {
    if (!issueToResolve) return;
    try {
      setIsResolving(true);
      await resolveCleaningReleasedPropertyIssue(issueToResolve.id);
      toast.success(t("admin.issuesHistory.resolved"));
      setIssueToResolve(null);
      await onResolved();
    } catch (error) {
      console.error("[CleaningIssueHistoryTable] resolve:", error);
      toast.error(error instanceof Error ? error.message : t("admin.issuesHistory.statusFailed"));
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <>
      <div className="border border-border/60 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-200 dark:bg-slate-800">
              <TableHead>{t("admin.issuesHistory.reportedAt")}</TableHead>
              <TableHead>{t("admin.issuesHistory.description")}</TableHead>
              <TableHead>{t("admin.issuesHistory.reporter")}</TableHead>
              <TableHead>{t("admin.issuesHistory.status")}</TableHead>
              {canEdit ? <TableHead className="text-right">{t("admin.issuesHistory.actions")}</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {issues.map((issue) => {
              const reporterName = issue.reporter?.full_name ?? t("common.unknown");
              const canResolve = canEdit && canCleaningManagementSelfResolve(issue);
              return (
                <TableRow key={issue.id}>
                  <TableCell>
                    {issue.created_at
                      ? new Date(issue.created_at).toLocaleString(dateLocale, {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : t("admin.issuesHistory.noDate")}
                  </TableCell>
                  <TableCell className="max-w-[220px]">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              "truncate cursor-default",
                              !issue.description ? "text-muted-foreground" : "",
                            )}
                            title={issue.description || ""}
                          >
                            {issue.description || t("admin.issuesHistory.noDescription")}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-sm whitespace-pre-wrap break-words">
                          {issue.description || t("admin.issuesHistory.noDescription")}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>{reporterName}</TableCell>
                  <TableCell>
                    <Badge
                      variant={issue.status === "resolved" ? "default" : "outline"}
                      className={issueStatusBadgeClass(issue.status)}
                    >
                      {statusText(issue.status)}
                    </Badge>
                  </TableCell>
                  {canEdit ? (
                    <TableCell className="text-right">
                      {canResolve ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setIssueToResolve(issue)}
                        >
                          {t("admin.issuesHistory.markResolved")}
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <AlertDialog
        open={issueToResolve !== null}
        onOpenChange={(open) => {
          if (!open && !isResolving) setIssueToResolve(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.issuesHistory.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.issuesHistory.confirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResolving}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={isResolving}
              onClick={(event) => {
                event.preventDefault();
                void handleConfirmResolve();
              }}
            >
              {isResolving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("admin.issuesHistory.markResolved")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
